import { Component, OnInit, ViewChild, Input, HostListener, ElementRef } from '@angular/core';
import { trigger, state, style, animate, transition } from '@angular/animations';
import pq from 'pqgrid';
import { ToastNotificationComponent } from '../toast-notification/toast-notification.component';
import { PqgridComponent } from '../pqgrid/pqgrid.component';
import { FormControl, FormGroup } from '@angular/forms';
import { debounceTime } from 'rxjs';
import { LoadBankDataService } from '../services/load-bank.service';
import { ListDataService } from '../services/list-data.service';
import { ContractDataService } from '../services/contract-data.service';
import { LoadBank } from '../models/LoadBank';
import { ListItem } from '../models/ListItem';
import { LoadBankGenerator } from '../models/LoadBankGenerator';
import { LoadBankType, LocationType } from '../models/Enums';
import { LocationListItem } from '../models/LocationListItem';
import { ContractParameters } from '../models/ContractParameters';
import { Dispatch } from '../models/Dispatch';
import { LocationSearchParameters } from '../models/LocationSearchParameters';
import { Contract } from '../models/Contract';
import { UserDataService } from '../services/user-data.service';
import { UserSettingsService } from '../services/user-settings.service';

@Component({
    selector: 'load-bank',
    templateUrl: 'load-bank.component.html',
    styleUrls: [],
    animations: [
        trigger('fadeInOut', [
            state('in', style({ opacity: 1 })),
            transition(':enter', [
                style({ opacity: 0 }),
                animate(300)
            ]),
            transition(':leave', [
                animate(0, style({ opacity: 0 }))
            ])
        ])
    ],
    standalone: false
})
export class LoadBankComponent implements OnInit {

    constructor(private _loadBankData: LoadBankDataService, private _listDataService: ListDataService, private _contractDataService: ContractDataService, private _userDataService: UserDataService, private _userSettingsService: UserSettingsService) {
    }
    @ViewChild('loadBankGrid') loadBankGrid: PqgridComponent;
    @ViewChild('toastNotification') toastNotificationComponent: ToastNotificationComponent;
    @ViewChild('locationDropdown') locationDropdownRef: ElementRef;
    @ViewChild('contractDropdown') contractDropdownRef: ElementRef;
    @Input() locCodeDropdown: string;
    errorMessage: string = '';
    loadBank: LoadBank = new LoadBank();
    refreshLocationType: string = '';
    loadBankList: LoadBank[] = [];
    loadBankGenerator: LoadBankGenerator = new LoadBankGenerator();
    showLoadGenerator: boolean = false;
    loadTemplateNumbers: string = '';
    currentLocCode: string = '';
    currentLoadBankType: LoadBankType = LoadBankType.Load;
    commodityList: ListItem[] = [];
    locationType: string = '';
    loadBankType: string = '';
    contractLocations: LocationListItem[] = [];
    filteredLocations: LocationListItem[] = [];
    locationList: LocationListItem[] = [];
    dispatch: Dispatch = new Dispatch();
    locationSearchParms: LocationSearchParameters = new LocationSearchParameters();
    contractList: Contract[] = [];
    locList: ListItem[] = [];
    locationSearchAll: boolean = false;
    locationSearchForm = this.createLocationSearchForm();
    zerosCount: number;
    isSelectedCount: number;
    showDeleteButton: boolean = false;
    showSpinnerDeleteButton: boolean = false;
    totalRecordCount: number = 0;
    showSpinnerAddButton: boolean = false;
    showDupsError: boolean = false;
    showLocationSearch: boolean = false;
    showContractSearch: boolean = false;

    loadBankColumns = [
        {
            title: "<input type='checkbox' id='selectAllCheckbox' class='delete-chkbox' style='margin-left: 10px;'/>",
            cb: {
                all: true,
                header: true
            },
            width: 25, type: "checkbox", dataIndx: "isSelected", resizable: false, editable: true, align: 'center',
            sortable: false, nodrag: true, dataType: "bool", editor: false
        },
        { title: "Load #", width: 100, dataType: "string", align: 'center', dataIndx: "loadNumber", editable: false, filter: { crules: [{ condition: "contain" }] } },
        { title: "Description", minWidth: 350, dataType: "string", dataIndx: "description", editable: false, filter: { crules: [{ condition: "contain" }] } },
        { title: "Comm", minWidth: 80, dataType: "string", align: 'center', dataIndx: "commodityCode", editable: false, filter: { crules: [{ condition: "contain" }] } },
        { title: "Contract #", width: 100, dataType: "string", align: 'center', dataIndx: "contractNumber", editable: false, filter: { crules: [{ condition: "contain" }] } },
        { title: "Origin", minWidth: 350, dataType: "string", dataIndx: "locationName", editable: false, filter: { crules: [{ condition: "contain" }] } },
        { title: "City", width: 100, dataType: "string", align: 'center', dataIndx: "locationCity", editable: false, filter: { crules: [{ condition: "contain" }] } },
        { title: "Customer", width: 250, dataType: "string", dataIndx: "customerName", editable: false, filter: { crules: [{ condition: "contain" }] } },
        { title: "Customer Code", width: 130, dataType: "string", align: 'center', dataIndx: "customerCode", editable: false, filter: { crules: [{ condition: "contain" }] } },
        { title: "Created By", width: 120, dataType: "string", align: 'center', dataIndx: "createdBy", editable: false, filter: { crules: [{ condition: "contain" }] } },
        { title: "Created Date", width: 100, dataType: "string", align: 'center', dataIndx: "createdDate", editable: false }
    ];

    loadBankOptions: pq.gridT.options = {
        menuIcon: false,
        showTop: false,
        roundCorners: true,
        reactive: true,
        locale: "en",
        height: "flex",
        fillHandle: "",
        freezeCols: 2,
        resizable: true,
        autoRow: false,
        numberCell: {
            show: false
        },
        colModel: this.loadBankColumns,
        animModel: {
            on: true
        },
        dataModel: {
            data: []
        },
        sortModel: {
            cancel: true,
            type: "local",
            single: false,
            space: false
        },
        filterModel: {
            header: true,
            menuIcon: false
        }
    }

    clearLocationDropdown(): void {
        this.locationSearchCustomer = "";
        this.locationSearchName = "";
        this.locationSearchCity = "";
    }

    @HostListener('document:click', ['$event'])
    onClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;

        const isLocationDropdown = this.locationDropdownRef?.nativeElement?.contains(target);
        const isContractDropdown = this.contractDropdownRef?.nativeElement?.contains(target);

        if (!isLocationDropdown) {
            this.showLocationSearch = false;
            this.clearLocationDropdown();
        }

        if (!isContractDropdown) {
            this.showContractSearch = false;
        }
    }

    toggleLocationSearch() {
        if (this.showLocationSearch) {
            this.showLocationSearch = false;
            this.clearLocationDropdown();
        }
        else {
            this.showLocationSearch = true;
            this.showContractSearch = false;
        }
    }

    toggleContractSearch() {
        if (this.showContractSearch) {
            this.showContractSearch = false;
        }
        else {
            this.showContractSearch = true;
            this.showLocationSearch = false;
        }
    }

    getLoadBankByLoc() {
        this._loadBankData.getLoadBankByLoc(this.loadBankGenerator.loadBankParameters).subscribe({
            next: data => {
                this.loadBankList = data;
                this.loadBankOptions.dataModel.data = data;
                this.totalRecordCount = data.length;
                this.clearLoadBankGenerator();
                this.setGridHeight();
            },
            error: err => this.errorMessage = err
        });
    }

    getCommodityList(locCode: string) {
        this.commodityList = [];
        if (locCode && locCode.length > 0) {
            this._listDataService.getCommodities(locCode).subscribe({
                next: data => {
                    this.commodityList = data;
                },
                error: err => this.errorMessage = err
            });
        }
    }

    refreshButtonClick() {
        this.setLocationType();
        this.getLoadBankByLoc();
        this.renameColumns();
        this.getCommodityList(this.loadBankGenerator.loadBankParameters.locCode);
        this._userSettingsService.setCurrentLocCode(this.loadBankGenerator.loadBankParameters.locCode);
    }

    toggleLoadBankGenerator() {
        this.showLoadGenerator = !this.showLoadGenerator;
    }

    concatenateLoadBuilder() {
        this.loadTemplateNumbers = '';
        for (let i = 0; i < this.loadBankGenerator.quantityBuilder; i++) {
            let stringLength = this.loadBankGenerator.numberBuilder.length;
            let stringAddition = (+this.loadBankGenerator.numberBuilder + +i).toString();

            this.loadTemplateNumbers += this.loadBankGenerator.prefixBuilder + stringAddition.padStart(stringLength, '0') + this.loadBankGenerator.suffixBuilder + '\n';
        }
    }

    private validateLoadGenerator() {
        let validLoadNumbers = false;
        this.loadBankGenerator.loadNumbers = this.loadTemplateNumbers.split('\n');

        // handle empty strings in the array
        for (let i = 0; i < this.loadBankGenerator.loadNumbers.length; i++) {
            if (this.loadBankGenerator.loadNumbers[i].trim() === '') {
                this.loadBankGenerator.loadNumbers.splice(i, 1);
                i--;
            }
        }

        // check for carriage return or white space in last item and remove
        let lastItemInLoadNumbers = this.loadBankGenerator.loadNumbers[this.loadBankGenerator.loadNumbers.length - 1];
        if (this.loadBankGenerator.loadNumbers.length > 0 && (lastItemInLoadNumbers === "" || lastItemInLoadNumbers === "\n")) {
            this.loadBankGenerator.loadNumbers.pop();
        }

        // check for dups
        const loadNumbersSet = new Set(this.loadBankGenerator.loadNumbers);
        const matchingLoadNumbers = this.loadBankList.filter(num2 => loadNumbersSet.has(num2.loadNumber));
        if (matchingLoadNumbers.length === 0) {
            validLoadNumbers = true;
        }

        return validLoadNumbers;
    }

    clearLoadBankGenerator() {
        this.loadBankGenerator = new LoadBankGenerator();
        this.loadTemplateNumbers = '';
        this.clearCommodity();
        this.clearLocation();
        this.clearContract();

        this.loadBankGenerator.loadBankParameters.locCode = this.currentLocCode;
        this.loadBankGenerator.loadBankParameters.loadBankType = this.currentLoadBankType;
    }

    saveLoadBankGenerator() {
        if (!this.validateLoadGenerator()) {
            console.log('Dup numbers, please check, etc.');
            this.showDupsError = true;
            this.showSpinnerAddButton = false;
            return;
        } else {
            console.log('No dups, good to go!');
            this.showDupsError = false;
            this.showSpinnerAddButton = true;
        }

        this._loadBankData.saveLoadBankGenerator(this.loadBankGenerator).subscribe({
            next: data => {
                this.loadBankGenerator = data;
                this.clearLoadBankGenerator();
                this.getLoadBankByLoc();
                this.toastNotificationComponent.showToast("LoadBank Numbers Added", 3000, "success");
                this.showLoadGenerator = false;
                this.showSpinnerAddButton = false;
            },
            error: err => {
                this.errorMessage = err;
                this.toastNotificationComponent.showToast("Failed to add LoadBank Numbers", 120000, "error");
                this.showSpinnerAddButton = false;
            }

        });
    }

    deleteSelectedLoadBankRecords() {
        this.showSpinnerDeleteButton = true;
        let selectedLoadBankIds: number[] = this.loadBankList
            .filter((record: any) => record.isSelected)
            .map((record: any) => record.loadBankId);

        this._loadBankData.deleteSelectedLoadBankRecords(selectedLoadBankIds).subscribe({
            next: result => {

                if (result) {
                    this.toastNotificationComponent.showToast("LoadBank Numbers Deleted", 3000, "success");
                    this.showDeleteButton = false;
                    this.showSpinnerDeleteButton = false;
                    this.isSelectedCount = 0;
                    this.getLoadBankByLoc();
                    this.loadBankGrid.grid.reset({ filter: true });
                }
                else {
                    this.toastNotificationComponent.showToast("Failed to delete LoadBank Numbers", 120000, "error");
                    this.showSpinnerDeleteButton = false;
                }
            },
            error: err => {
                this.errorMessage = err;
                this.showSpinnerDeleteButton = false;
            }
        });
    }

    onPaste(event: ClipboardEvent) {
        event.preventDefault();

        const delimiters = [',', '.', ';', '|', ' ', '\n'];
        let delimiter: string | null = null;
        let pastedText = event.clipboardData.getData('text');

        for (const char of delimiters) {
            if (pastedText.includes(char)) {
                delimiter = char;
                break;
            }
        }

        if (delimiter !== null && delimiter !== '') {
            this.loadTemplateNumbers += pastedText
                .split(delimiter)
                .map(str => str.trim())
                .filter(str => str !== '')
                .join('\n');
        } else {
            this.loadTemplateNumbers += pastedText.trim();
        }
    }

    setLocationType() {
        if (this.loadBankGenerator.loadBankParameters.loadBankType === LoadBankType.Load) {
            this.locationType = "Origin";
            this.loadBankType = "Load";
        }
        else {
            this.locationType = "Destination";
            this.loadBankType = "Unload";
        }
        this.parameterOnChange();
    }

    get locationSearchCustomer(): string {
        return this.locationSearchParms.customerName;
    }
    set locationSearchCustomer(value: string) {
        this.locationSearchParms.customerName = value;
        this.filterContractLocations();
    }
    get locationSearchName(): string {
        return this.locationSearchParms.locationName;
    }
    set locationSearchName(value: string) {
        this.locationSearchParms.locationName = value;
        this.filterContractLocations();
    }
    get locationSearchCity(): string {
        return this.locationSearchParms.city;
    }
    set locationSearchCity(value: string) {
        this.locationSearchParms.city = value;
        this.filterContractLocations();
    }

    selectCommodity(commodity: ListItem): void {
        this.loadBankGenerator.commodityCode = commodity.value;
    }

    clearCommodity(): void {
        this.loadBankGenerator.commodityCode = '';
    }

    clearLocation(): void {
        this.locationSearchForm = this.createLocationSearchForm();
        //this.locationList = [];
        this.loadBankGenerator.locationId = 0;
        this.loadBankGenerator.locationName = '';
        this.loadBankGenerator.locationCity = '';
        this.loadBankGenerator.customerId = 0;
        this.loadBankGenerator.customerName = '';
        this.locationSearchAll = false;
    }

    clearContract(): void {
        //this.contractList = [];
        this.loadBankGenerator.contractId = 0;
        this.loadBankGenerator.contractNumber = '';
        this.loadBankGenerator.customerContract = '';
        this.loadBankGenerator.shipFrom = '';
        this.loadBankGenerator.shipTo = '';
        this.showLocationSearch = false;
        this.showContractSearch = false;
    }

    createLocationSearchForm() {
        const locationSearchForm = new FormGroup({
            locationName: new FormControl(''),
            city: new FormControl(''),
            customerName: new FormControl('')
        });

        locationSearchForm.valueChanges
            .pipe(debounceTime(1000))
            .subscribe(() => {
                this.searchLocationsAll();
            });
        return locationSearchForm;
    }

    searchLocationsAll() {
        if (this.locationSearchForm.controls.locationName.value.length > 1 ||
            this.locationSearchForm.controls.city.value.length > 1) {
            this._listDataService.searchLocations(this.locationSearchForm.getRawValue()).subscribe({
                next: data => {
                    this.locationList = data;
                },
                error: err => this.errorMessage = err
            });
        }
        else {
            this.locationList = [];
        }
    }

    getContractLocations() {
        this.clearContract();
        let parms: ContractParameters = new ContractParameters();
        parms.locCode = this.loadBankGenerator.loadBankParameters.locCode;
        parms.commodityCode = this.loadBankGenerator.commodityCode;
        parms.locationType = this.loadBankGenerator.loadBankParameters.loadBankType == 0 ? LocationType.Origin : LocationType.Destination;
        this._contractDataService.getContractLocations(parms).subscribe({
            next: data => {
                this.contractLocations = data;
                this.filteredLocations = data;
            },
            error: err => this.errorMessage = err
        });
    }

    filterContractLocations() {
        let filteredList = [...this.contractLocations];

        let customer = this.locationSearchParms.customerName.toLowerCase();
        let location = this.locationSearchParms.locationName.toLowerCase();
        let city = this.locationSearchParms.city.toLowerCase();

        if (customer.length > 0) {
            filteredList = filteredList.filter(item => item.customerName.toLowerCase().includes(customer));
        }
        if (location.length > 0) {
            filteredList = filteredList.filter(item => item.locationName.toLowerCase().includes(location));
        }
        if (city.length > 0) {
            filteredList = filteredList.filter(item => item.city.toLowerCase().includes(city));
        }

        this.filteredLocations = filteredList;
    }

    selectLocation(location: LocationListItem): void {
        //this.clearContract();
        this.loadBankGenerator.locationId = location.locationId;
        this.loadBankGenerator.locationName = location.locationName;
        this.loadBankGenerator.locationCity = location.city;
        this.loadBankGenerator.customerId = location.customerId;
        this.loadBankGenerator.customerName = location.customerName;
        this.getContracts();
        this.showLocationSearch = false;
        this.showContractSearch = false;
        this.clearLocationDropdown();
    }

    getContracts() {
        let contractParms = new ContractParameters();
        contractParms.locCode = this.loadBankGenerator.loadBankParameters.locCode;
        contractParms.locationType = this.loadBankGenerator.loadBankParameters.loadBankType == LoadBankType.Load ? LocationType.Origin : LocationType.Destination;
        contractParms.commodityCode = this.loadBankGenerator.commodityCode;
        contractParms.locationId = this.loadBankGenerator.locationId;
        contractParms.customerId = this.loadBankGenerator.customerId;

        this._contractDataService.getOpenContracts(contractParms).subscribe({
            next: data => {
                this.contractList = data;
            },
            error: err => this.errorMessage = err
        });
    }

    selectContract(contract: Contract) {
        this.loadBankGenerator.contractId = contract.contractId;
        this.loadBankGenerator.contractNumber = contract.contractNumber;
        this.loadBankGenerator.customerContract = contract.customerContract;
        this.loadBankGenerator.shipFrom = contract.periodBeginDate;
        this.loadBankGenerator.shipTo = contract.periodEndDate;
        this.toggleContractSearch();
    }

    resetCommodity(): void {
        this.clearCommodity();
        this.commodityOnChange();
    }

    commodityOnChange(): void {
        this.clearLocation();
        this.clearContract();
        this.getContractLocations();
        this.showContractSearch = false;
        this.showLocationSearch = false;
        this.clearLocationDropdown()
    }

    resetLocation(): void {
        this.clearLocation();
        this.locationOnChange();
    }

    locationOnChange(): void {
        this.clearContract();
        this.getContracts();
        this.showContractSearch = false;
        this.showLocationSearch = false;
        this.clearLocationDropdown();
    }

    parameterOnChange(): void {
        if (this.loadBankGenerator.loadBankParameters.locCode != '') {
            this.currentLocCode = this.loadBankGenerator.loadBankParameters.locCode;
            this.currentLoadBankType = this.loadBankGenerator.loadBankParameters.loadBankType;
        }
    }

    renameColumns() {
        let loadBankTypeL = 'Load #';
        let loadBankTypeU = 'Unload #';
        let locationTypeO = 'Origin';
        let locationTypeD = 'Destination';

        if (this.currentLoadBankType === LoadBankType.Unload) {
            for (let column of this.loadBankColumns) {
                if (column.title == loadBankTypeL) {
                    column.title = loadBankTypeU;
                }
                if (column.title == locationTypeO) {
                    column.title = locationTypeD;
                }
            }
        } else if (this.currentLoadBankType === LoadBankType.Load) {
            for (let column of this.loadBankColumns) {
                if (column.title == loadBankTypeU) {
                    column.title = loadBankTypeL;
                }
                if (column.title == locationTypeD) {
                    column.title = locationTypeO;
                }
            }
        }
    }


    isRowSelected() {
        let selecteditems = (this.loadBankOptions.dataModel.data as LoadBank[]).filter(item => item.isSelected);
        if (selecteditems.length > 0) {
            this.showDeleteButton = true;
            this.isSelectedCount = selecteditems.length;
        } else {
            this.showDeleteButton = false;
        }
    }

    setGridHeight() {
        if (this.loadBankGrid) {
            let gridPosition = this.loadBankGrid.div.nativeElement.getBoundingClientRect();
            this.loadBankOptions.height = window.innerHeight - gridPosition.top - 15;
        }
    }

    updateTotalRecords() {
        this.totalRecordCount = this.loadBankGrid.grid.option('dataModel.data').length;
    }

    ngOnInit(): void {
        let self = this;
        const sessionStoreLocCode = sessionStorage.getItem("locCode");

        this._listDataService.getListItems("loc").subscribe({
            next: data => {
                this.locList = data;
            },
            error: err => this.errorMessage = err
        });

        this._userDataService.getUserSettings().subscribe({
            next: data => {
                this.currentLoadBankType = this.loadBankGenerator.loadBankParameters.loadBankType;
                if (sessionStoreLocCode === null || sessionStoreLocCode === "") {
                    this.loadBankGenerator.loadBankParameters.locCode = data.defaultLocCode;
                    this.currentLocCode = this.loadBankGenerator.loadBankParameters.locCode;
                } else {
                    this.loadBankGenerator.loadBankParameters.locCode = sessionStoreLocCode;
                    this.currentLocCode = sessionStoreLocCode;
                }
                this.getLoadBankByLoc();
                this.getCommodityList(this.loadBankGenerator.loadBankParameters.locCode);
            },
            error: err => this.errorMessage = err
        });

        this.loadBankOptions.change = function () {
            self.isRowSelected();
        }

        window.addEventListener('resize', () =>
            this.setGridHeight());

        this.setLocationType();
    }

    ngAfterViewInit() {
        this.loadBankGrid.grid.on("filter", () => {
            this.updateTotalRecords();
        });
    }

    ngOnDestory(): void {
        this.loadBankGrid.grid.off("filter");
    }
}
